import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import PricingCard from "../pricing/PricingCard";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";

interface Plan {
  name: string;
  price?: string;
  monthlyPrice?: string;
  annualPrice?: string;
  monthlyPriceCAD?: string;
  annualPriceCAD?: string;
  features: string[];
  popular?: boolean;
  buttonText: string;
  icon?: string;
  description?: string;
  subtitle?: string;
  upgradeNote?: string;
  isPro?: boolean;
}

const PricingSection: React.FC = () => {
  const { t } = useTranslation();

  const plans =
    (t("pricingPage.plans", { returnObjects: true }) as Plan[]) || [];

  const title = t("pricingPage.title");
  const subtitle = t("pricingPage.subtitle");
  const note = t("pricingPage.note");

  const securityBanner = t("pricingPage.securityBanner", {
    returnObjects: true,
  }) as {
    title: string;
    subtitle: string;
    features: string[];
  };

  if (!Array.isArray(plans)) {
    return <div>Error loading pricing plans.</div>;
  }

  return (
    <section className="py-32 relative bg-white !overflow-visible z-10">
      {/* Asymmetric Torn Paper Effect Top */}
      <div
        className="absolute top-0 left-0 w-full h-24 bg-white z-20"
        style={{
          clipPath:
            "polygon(0% 0%, 100% 0%, 100% 100%, 92% 65%, 85% 90%, 78% 60%, 70% 95%, 62% 55%, 55% 90%, 48% 60%, 40% 95%, 32% 55%, 25% 90%, 18% 60%, 10% 95%, 0% 100%)",
          transform: "translateY(-98%) scaleY(-1)",
        }}
      ></div>

      {/* Asymmetric Torn Paper Effect Bottom */}
      <div
        className="absolute bottom-0 left-0 w-full h-24 bg-white z-20"
        style={{
          clipPath:
            "polygon(0% 0%, 100% 0%, 100% 100%, 95% 60%, 88% 95%, 80% 55%, 72% 90%, 65% 50%, 58% 95%, 50% 55%, 42% 90%, 35% 50%, 28% 95%, 20% 55%, 12% 90%, 5% 60%, 0% 100%)",
          transform: "translateY(98%)",
        }}
      ></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-30">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#00342d] mb-6 tracking-tight"
            style={{ fontFamily: "'Bruno Ace', sans-serif" }}
          >
            {title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 leading-relaxed"
          >
            {subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-screen-2xl mx-auto mb-20 !overflow-visible p-4 items-start">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative !overflow-visible ${
                plan.popular ? "lg:-mt-4 lg:mb-4 z-10" : "z-0"
              }`}
            >
              <PricingCard plan={plan} />
            </motion.div>
          ))}
        </div>

        {/* Security Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto bg-[#f0fdf9] rounded-3xl p-8 md:p-12 border border-[#00e9be]/20"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-[#00e9be]/10 flex items-center justify-center">
                <ShieldCheckIcon className="w-10 h-10 text-[#00342d]" />
              </div>
            </div>
            <div className="flex-grow text-center md:text-left">
              <h3 className="text-2xl font-bold text-[#00342d] mb-2">
                {securityBanner.title}
              </h3>
              <p className="text-gray-600 mb-6">{securityBanner.subtitle}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {securityBanner.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#00e9be]" />
                    <span className="text-sm font-medium text-[#00342d]">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <p className="text-center text-gray-500 mt-12 text-sm max-w-2xl mx-auto">
          {note}
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
